import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FiTruck,
  FiPackage,
  FiStar,
} from "react-icons/fi";
import { FaWhatsapp, FaAmazon } from "react-icons/fa";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

const MARQUEE = [
  "Packaging Solutions",
  "Business Procurement",
  "Distribution Services",
  "Supply Chain Support",
  "Operational Excellence",
  "Industry-Focused Solutions",
  "Strategic Sourcing",
  "Business Supply Network",
  "Enterprise Services",
  "Nationwide Distribution",
];

const TRUST = [
  { Icon: FiTruck,   label: "Pan India Distribution",    color: "#3B82F6" },
  { Icon: FiPackage, label: "Enterprise Supply Partner",  color: "#FF6B2B" },
  { Icon: FiStar,    label: "eCommerce Fulfilment Ready", color: "#FFB800" },
];

const BADGES = [
  { text: "Packaging Solutions",   sub: "Quality Driven",       top: "5%",    left:  "5%" },
  { text: "Business Procurement",  sub: "Reliable Sourcing",    top: "5%",    right: "5%" },
  { text: "Supply Network",        sub: "Nationwide Reach",     bottom: "10%",left:  "5%" },
  { text: "Industry Focused",      sub: "Multi-Sector Support", bottom: "10%",right: "5%" },
];

/* ── animation helpers ── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 44 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.82, delay, ease: [0.22, 1, 0.36, 1] },
});

/* split headline into word spans, each animating in */
function AnimatedHeadline({ children, delay = 0 }) {
  const words = children.split(" ");
  return (
    <span style={{ display: "inline" }}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 28, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
          transition={{
            duration: 0.55,
            delay: delay + i * 0.07,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{ display: "inline-block", marginRight: "0.28em" }}
        >
          {word}
        </motion.span>
      ))}
    </span>
  );
}

/* character-by-character reveal for the gradient line */
function AnimatedGradientText({ children, delay = 0 }) {
  const chars = children.split("");
  return (
    <span
      style={{
        background: "linear-gradient(135deg,#E91E8C,#FF6B2B,#FFD700)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        display: "inline-block",
      }}
    >
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.4,
            delay: delay + i * 0.04,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{ display: "inline-block", whiteSpace: "pre" }}
        >
          {ch}
        </motion.span>
      ))}
    </span>
  );
}

/* body text: reveals word by word with a subtle slide */
function AnimatedBody({ children, delay = 0, style = {} }) {
  const words = children.split(" ");
  return (
    <p style={style}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.4,
            delay: delay + i * 0.025,
            ease: "easeOut",
          }}
          style={{ display: "inline-block", marginRight: "0.28em" }}
        >
          {word}
        </motion.span>
      ))}
    </p>
  );
}

export default function Hero() {
  const orbRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!orbRef.current) return;
      const x = (e.clientX / window.innerWidth  - 0.5) * 18;
      const y = (e.clientY / window.innerHeight - 0.5) * 14;
      orbRef.current.style.transform = `translate(${x}px,${y}px)`;
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <section
      id="home"
      style={{
        minHeight: "100vh",
        paddingTop: "90px",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(150deg,#fff 0%,#fff5f9 35%,#fffbe6 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="dot-grid" style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />

      {/* Parallax blobs */}
      <div
        ref={orbRef}
        style={{ position: "absolute", inset: 0, pointerEvents: "none", transition: "transform 0.4s ease" }}
      >
        <div style={{
          position: "absolute", top: "8%", right: "4%",
          width: "clamp(250px,35vw,500px)", height: "clamp(250px,35vw,500px)",
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(233,30,140,0.1) 0%,rgba(255,107,43,0.05) 45%,transparent 70%)",
          filter: "blur(60px)", animation: "float 8s ease-in-out infinite",
        }} />
        <div style={{
          position: "absolute", bottom: "12%", left: "2%",
          width: "clamp(180px,22vw,350px)", height: "clamp(180px,22vw,350px)",
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(255,215,0,0.08) 0%,transparent 70%)",
          filter: "blur(50px)", animation: "float 10s ease-in-out infinite 1.5s",
        }} />
      </div>

      {/* Main content */}
      <div
        className="container"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          position: "relative",
          zIndex: 2,
          padding: "0 20px",
        }}
      >
        <div
          className="hero-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 0.9fr",
            gap: "clamp(1.5rem,4vw,4rem)",
            alignItems: "center",
            width: "100%",
            maxWidth: "1280px",
            margin: "0 auto",
          }}
        >
          {/* ── LEFT ── */}
          <div>
            {/* Headline */}
            <motion.h1
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "clamp(2rem,4.5vw,4.5rem)",
                lineHeight: 1.08,
                color: "var(--text)",
                marginBottom: "0.5rem",
              }}
            >
              <AnimatedHeadline delay={0.15}>India's Trusted</AnimatedHeadline>
              <br />
              <AnimatedGradientText delay={0.32}>Packaging &amp; Supply</AnimatedGradientText>
              <br />
              <motion.em
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.65, delay: 0.72, ease: [0.22, 1, 0.36, 1] }}
                style={{ fontStyle: "italic" }}
              >
                &amp; Solutions Company
              </motion.em>
            </motion.h1>

            {/* Subtitle label */}
            <motion.div {...fadeUp(0.85)} style={{ marginBottom: "1.2rem" }}>
              <p style={{
                fontFamily: "var(--font-accent)",
                fontSize: "clamp(0.72rem,1.5vw,1rem)",
                color: "var(--text-mid)",
                letterSpacing: "0.06em",
                marginBottom: "0.4rem",
              }}>
                PACKAGING SOLUTIONS • PROCUREMENT • DISTRIBUTION SERVICES
              </p>
              <AnimatedBody
                delay={0.95}
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "clamp(0.8rem,1.2vw,0.95rem)",
                  color: "var(--text-muted)",
                  lineHeight: 1.7,
                  maxWidth: 580,
                  margin: 0,
                }}
              >
                SPE provides integrated packaging, procurement, and distribution solutions for businesses across multiple industries. Through a commitment to quality, reliability, and operational excellence, we help organizations meet their sourcing and supply requirements efficiently.
              </AnimatedBody>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              {...fadeUp(1.15)}
              style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.5rem" }}
            >
              {TRUST.map(({ Icon, label, color }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 1.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.35rem",
                    background: "#fff",
                    border: "1.5px solid rgba(233,30,140,0.1)",
                    borderRadius: 99,
                    padding: "0.3rem 0.8rem",
                    boxShadow: "var(--shadow-sm)",
                  }}
                >
                  <Icon size={12} style={{ color }} />
                  <span style={{
                    fontFamily: "var(--font-body)", fontSize: "0.7rem",
                    fontWeight: 600, color: "var(--text-mid)",
                  }}>
                    {label}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTAs */}
            <motion.div
              {...fadeUp(1.45)}
              style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "2rem" }}
            >
              <motion.a
                href="https://wa.me/919821991932?text=Hi, I want bulk disposable products (please share size and quantity)"
                target="_blank" rel="noreferrer"
                whileHover={{ scale: 1.04, boxShadow: "0 12px 30px rgba(233,30,140,0.4)" }}
                whileTap={{ scale: 0.97 }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.5rem",
                  padding: "0.75rem 1.8rem",
                  background: "linear-gradient(135deg,#E91E8C,#FF6B2B)",
                  color: "#fff", border: "none", borderRadius: 99,
                  cursor: "pointer", fontFamily: "var(--font-body)",
                  fontWeight: 700, fontSize: "0.9rem", textDecoration: "none",
                  boxShadow: "0 6px 20px rgba(233,30,140,0.35)",
                }}
              >
                <FaWhatsapp size={17} /> Request Quotation
              </motion.a>
              <motion.a
                href="https://www.flipkart.com/your-product-link"
                target="_blank" rel="noreferrer"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.5rem",
                  padding: "0.75rem 1.5rem",
                  background: "#fff", color: "#FF9900",
                  border: "2px solid rgba(255,153,0,0.3)",
                  borderRadius: 99, fontFamily: "var(--font-body)",
                  fontWeight: 700, fontSize: "0.9rem",
                  textDecoration: "none", cursor: "pointer",
                }}
              >
                <FaAmazon size={17} /> Explore Solutions
              </motion.a>
            </motion.div>
          </div>

          {/* ── RIGHT — Lottie ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              position: "relative", width: "100%", height: "100%",
            }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute",
                width: "90%", height: "90%",
                maxWidth: "420px", maxHeight: "420px",
                borderRadius: "50%",
                border: "2px dashed rgba(233,30,140,0.15)",
              }}
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              style={{
                position: "absolute",
                width: "75%", height: "75%",
                maxWidth: "350px", maxHeight: "350px",
                borderRadius: "50%",
                border: "1.5px dashed rgba(255,107,43,0.12)",
              }}
            />
            <motion.div
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 3.5, repeat: Infinity }}
              style={{
                position: "absolute",
                width: "70%", height: "70%",
                borderRadius: "50%",
                background: "radial-gradient(circle,rgba(233,30,140,0.15) 0%,rgba(255,107,43,0.08) 50%,transparent 70%)",
                filter: "blur(30px)",
              }}
            />

            <div style={{
              position: "relative", zIndex: 5,
              width: "clamp(220px,35vw,500px)",
              height: "clamp(220px,35vw,500px)",
            }}>
              <DotLottieReact
                src="https://lottie.host/d24588b6-de94-488a-b6bc-0aa4d5f8bedc/6af1OqH0dS.lottie"
                loop autoplay
                style={{
                  width: "100%", height: "100%",
                  filter: "drop-shadow(0 10px 30px rgba(233,30,140,0.3))",
                }}
              />
            </div>

            {BADGES.map(({ text, sub, ...pos }, i) => (
              <motion.div
                key={text}
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3 + i * 0.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                style={{
                  position: "absolute", ...pos, zIndex: 6,
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(12px)",
                  border: "1.5px solid rgba(233,30,140,0.15)",
                  borderRadius: 12,
                  padding: "0.5rem 0.85rem",
                  boxShadow: "0 6px 20px rgba(233,30,140,0.1)",
                  whiteSpace: "nowrap",
                }}
              >
                <p style={{
                  fontFamily: "var(--font-body)", fontWeight: 700, fontSize: "0.7rem",
                  background: "linear-gradient(135deg,#E91E8C,#FF6B2B)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  backgroundClip: "text", margin: 0,
                }}>
                  {text}
                </p>
                <p style={{
                  fontFamily: "var(--font-body)", fontSize: "0.6rem",
                  color: "var(--text-muted)", marginTop: 1, marginBottom: 0,
                }}>
                  {sub}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Marquee strip */}
      <div style={{
        background: "linear-gradient(135deg,#E91E8C,#FF6B2B,#FFD700)",
        padding: "0.5rem 0", overflow: "hidden", flexShrink: 0, zIndex: 2,
      }}>
        <div style={{
          display: "flex", width: "max-content",
          animation: "marquee 26s linear infinite",
        }}>
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((item, i) => (
            <span key={i} style={{
              fontFamily: "var(--font-body)", fontSize: "0.75rem",
              fontWeight: 700, color: "rgba(255,255,255,0.93)",
              padding: "0 2.5rem", whiteSpace: "nowrap", letterSpacing: "0.04em",
            }}>
              ✦ {item}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        /* ── Responsive ── */
        @media (max-width: 900px) {
          .hero-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding-top: 1rem;
            padding-bottom: 1rem;
          }
          .hero-grid > *:last-child {
            order: -1;
          }
          .hero-grid > *:last-child > div[style*="clamp(220px"] {
            width: clamp(180px, 55vw, 320px) !important;
            height: clamp(180px, 55vw, 320px) !important;
          }
        }

        @media (max-width: 480px) {
          .hero-grid > *:last-child {
            display: none !important;
          }
        }

        /* hide corner badges on small screens */
        @media (max-width: 640px) {
          .hero-grid > *:last-child > div[style*="position: absolute"][style*="zIndex: 6"] {
            display: none !important;
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-20px); }
        }
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
      `}</style>
    </section>
  );
}